"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { getSession } from "next-auth/react";
import { createPortal } from "react-dom";
import {
  Search,
  Loader2,
  TrendingUp,
  MapPin,
  Users,
  Briefcase,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/telemetry/logger";

interface SearchResult {
  type: "property" | "investor" | "deal";
  id: number;
  title: string;
  description: string;
  relevance_score: number;
  tags: string[];
  data: Record<string, unknown>;
}

interface SearchResponse {
  query: string;
  total_results: number;
  returned: number;
  results: SearchResult[];
}

interface Suggestion {
  text: string;
  icon: React.ReactNode;
  type: "suggestion" | "trending" | "recent";
}

interface SearchCommandPaletteProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function SearchCommandPalette({
  isOpen = true,
  onClose,
}: SearchCommandPaletteProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track if component is mounted for portal
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";

      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }

    return () => {
      // Restore body scroll when modal closes
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Fetch suggestions and trending
  const loadSuggestions = useCallback(async () => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery || trimmedQuery.length < 2) {
      // Load trending for empty or single character queries
      try {
        const session = await getSession();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const token = (session as any)?.accessToken;

        const res = await fetch("/api/v1/search/trending?limit=5", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          logger.warn("Failed to load trending", { status: res.status, statusText: res.statusText });
          setSuggestions([]);
          return;
        }

        const data = await res.json();
        if (data.trending && Array.isArray(data.trending)) {
          setSuggestions(
            data.trending.map((trend: string) => ({
              text: trend,
              icon: <TrendingUp className="w-4 h-4" />,
              type: "trending",
            })),
          );
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        logger.error("Failed to load trending", error instanceof Error ? error : undefined);
        setSuggestions([]);
      }
      return;
    }

    // Load autocomplete suggestions (requires 2+ characters)
    try {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      const res = await fetch(
        `/api/v1/search/autocomplete?q=${encodeURIComponent(trimmedQuery)}&limit=8`,
        {
          signal: abortControllerRef.current.signal,
        },
      );

      if (!res.ok) {
        logger.warn("Failed to load autocomplete", { status: res.status, statusText: res.statusText });
        setSuggestions([]);
        return;
      }

      const data = await res.json();

      if (data.suggestions && Array.isArray(data.suggestions)) {
        setSuggestions(
          data.suggestions.map((sugg: string) => ({
            text: sugg,
            icon: <Search className="w-4 h-4" />,
            type: "suggestion",
          })),
        );
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        logger.error("Failed to load suggestions", error instanceof Error ? error : undefined);
      }
    }
  }, [query]);

  // Fetch search results
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      const res = await fetch(
        `/api/v1/search?q=${encodeURIComponent(searchQuery)}&limit=10`,
        {
          signal: abortControllerRef.current.signal,
        },
      );

      if (!res.ok) throw new Error("Search failed");

      const data: SearchResponse = await res.json();
      setResults(data.results);
      setSelectedIndex(0);
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        logger.error("Search error", error instanceof Error ? error : undefined);
        setResults([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Load suggestions when query changes
  useEffect(() => {
    loadSuggestions();
  }, [query, loadSuggestions]);

  const handleSelect = (result: SearchResult) => {
    const routes: Record<string, string> = {
      property: `/properties/${result.id}`,
      investor: `/investors/${result.id}`,
      deal: `/deals/${result.id}`,
    };

    if (routes[result.type]) {
      router.push(routes[result.type]);
      handleClose();
      setQuery("");
    }
  };

  const handleSuggestionSelect = (suggestion: Suggestion) => {
    setQuery(suggestion.text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allItems = [...results, ...suggestions];

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % allItems.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + allItems.length) % allItems.length);
        break;
      case "Enter":
        e.preventDefault();
        if (results.length > 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]);
        } else if (suggestions.length > 0) {
          const suggestionIndex = selectedIndex - results.length;
          if (suggestionIndex >= 0 && suggestionIndex < suggestions.length) {
            handleSuggestionSelect(suggestions[suggestionIndex]);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
    }
  };

  const getResultIcon = (type: string) => {
    const iconClass = "w-4 h-4";
    switch (type) {
      case "property":
        return <MapPin className={iconClass} />;
      case "investor":
        return <Users className={iconClass} />;
      case "deal":
        return <Briefcase className={iconClass} />;
      default:
        return <Search className={iconClass} />;
    }
  };

  // Render modal content
  const modalContent = isOpen ? (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[10vh] bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div ref={containerRef} className="w-full max-w-2xl mx-4">
        <div className="bg-background rounded-xl border border-border shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="relative border-b border-border p-4">
            <Search className="absolute left-8 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search properties, investors, deals... Press ESC to close"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full bg-transparent pl-14 pr-12 py-3 text-lg outline-none placeholder:text-muted-foreground focus:ring-0"
              autoFocus
              autoComplete="off"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                type="button"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading && (
              <div className="p-8 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            )}

            {!isLoading && results.length > 0 && (
              <div>
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                  Results ({results.length})
                </div>
                {results.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSelect(result)}
                    className={cn(
                      "w-full text-left px-4 py-3 border-b border-border/50 hover:bg-accent transition-colors",
                      selectedIndex === index && "bg-accent",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1 text-muted-foreground flex-shrink-0">
                        {getResultIcon(result.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-foreground flex items-center gap-2">
                          {result.title}
                          {result.tags.length > 0 && (
                            <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                              {result.tags[0]}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {result.description}
                        </div>
                        {result.data.price ? (
                          <div className="text-sm font-semibold text-primary mt-1">
                            ${(result.data.price as number).toLocaleString()}
                          </div>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground flex-shrink-0">
                        {Math.round(result.relevance_score)}%
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Suggestions */}
            {!isLoading && results.length === 0 && suggestions.length > 0 && (
              <div>
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                  {!query ? "Trending" : "Suggestions"}
                </div>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.text}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    className={cn(
                      "w-full text-left px-4 py-3 border-b border-border/50 hover:bg-accent transition-colors flex items-center gap-3",
                      selectedIndex === results.length + index && "bg-accent",
                    )}
                  >
                    <div className="text-muted-foreground">
                      {suggestion.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-foreground">
                        {suggestion.text}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">↵</div>
                  </button>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading &&
              results.length === 0 &&
              suggestions.length === 0 &&
              query.length >= 2 && (
                <div className="p-8 text-center text-muted-foreground">
                  <Search className="w-8 h-8 mx-auto mb-3 opacity-50" />
                  <p>No results found for &ldquo;{query}&rdquo;</p>
                  <p className="text-xs mt-2">Try a different search term</p>
                </div>
              )}

            {/* Initial State */}
            {!isLoading &&
              results.length === 0 &&
              suggestions.length === 0 &&
              query.length < 2 && (
                <div className="p-8 text-center text-muted-foreground">
                  <div className="text-xs uppercase tracking-wider font-semibold mb-4">
                    Quick Tips
                  </div>
                  <ul className="text-sm space-y-2 text-left">
                    <li>
                      💰 Search by price: &ldquo;Denver homes under 500k&rdquo;
                    </li>
                    <li>
                      🏠 Search by property type: &ldquo;3 bed ranch
                      houses&rdquo;
                    </li>
                    <li>
                      👤 Find investors: &ldquo;John investment Denver&rdquo;
                    </li>
                    <li>📊 Explore deals: &ldquo;BRRR strategy&rdquo;</li>
                  </ul>
                </div>
              )}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-4 py-3 bg-muted/30 text-xs text-muted-foreground flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>↑↓ Navigate</span>
              <span>↵ Select</span>
              <span>ESC Close</span>
            </div>
            <div className="text-xs">
              Powered by PropCafe Enterprise Search
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // Use portal to render at document root if mounted
  if (!mounted) return null;

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
}
